import { twMerge } from "tailwind-merge";
import { Skeleton } from "../../Skeleton";
import type { CellComponentProps, CellEmphasis, LinkBehavior } from "../DataTable.types";
import {
  getCellContainerClasses,
  getCellTextClasses,
  getNumericColorClasses,
  getSkeletonSize,
  resolveValue,
  wrapWithLink,
} from "./cellUtils";

export interface NumberCellProps<T = any> extends CellComponentProps<T> {
  format?: "number" | "currency" | "percent" | "compact";
  decimals?: number;
  prefix?: string;
  suffix?: string;
  emptyText?: string;
  align?: "left" | "center" | "right";
  emphasis?: CellEmphasis;
  href?: string | ((row: T) => string);
  linkBehavior?: LinkBehavior;
  showPositiveSign?: boolean;
  colorCode?: boolean; // Green for positive, red for negative
  className?: string;
}

/**
 * NumberCell
 *
 * DataTable cell component for displaying numeric values.
 * Supports currency, percentage, and compact formatting with optional color coding for positive/negative values.
 */
export function NumberCell<T = any>({
  value,
  row,
  context,
  format = "number",
  decimals = 0,
  prefix,
  suffix,
  emptyText = "—",
  align = "right",
  emphasis = "normal",
  href,
  linkBehavior = "hover",
  showPositiveSign = false,
  colorCode = false,
  className,
}: NumberCellProps<T>) {
  const { isLoading } = context;

  if (isLoading) {
    return <Skeleton className={getSkeletonSize(emphasis)} />;
  }

  if (value === null || value === undefined || isNaN(Number(value))) {
    return <div className={getCellContainerClasses(align, "text-text-muted")}>{emptyText}</div>;
  }

  const numValue = Number(value);
  let formattedValue: string;

  switch (format) {
    case "currency":
      formattedValue = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(numValue);
      break;
    case "percent":
      formattedValue = new Intl.NumberFormat("en-US", {
        style: "percent",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(numValue / 100);
      break;
    case "compact":
      formattedValue = new Intl.NumberFormat("en-US", {
        notation: "compact",
        minimumFractionDigits: decimals > 0 ? 1 : 0,
        maximumFractionDigits: decimals,
      }).format(numValue);
      break;
    default:
      formattedValue = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(numValue);
  }

  const sign = showPositiveSign && numValue > 0 ? "+" : "";
  const displayValue = `${sign}${formattedValue}`;

  // Combine emphasis classes with color coding if enabled
  const textClasses = colorCode ? getNumericColorClasses(numValue, true) : getCellTextClasses(emphasis);

  const containerClasses = getCellContainerClasses(align, twMerge(textClasses, className));

  const content = (
    <>
      {prefix && <span className="text-text-muted">{prefix}</span>}
      <span>{displayValue}</span>
      {suffix && <span className="text-text-muted">{suffix}</span>}
    </>
  );

  // If href is provided, wrap with link behavior
  const resolvedHref = resolveValue(href, row);
  if (resolvedHref) {
    return <div className={containerClasses}>{wrapWithLink(content, resolvedHref, row, linkBehavior)}</div>;
  }

  return <div className={containerClasses}>{content}</div>;
}
